// Where an auth flow may send the user afterwards. app and next are separate
// sign-in origins: a link may switch between them, but only to these two and
// only to paths of the dashboard itself.
export const DASHBOARD_ORIGINS = ['https://app.gastronaut.ai', 'https://next.gastronaut.ai'];

const AUTH_PATHS = ['/auth', '/__/auth', '/loginWithGastronaut'];
const MAX_PATH_LENGTH = 512;
const PLACEHOLDER_ORIGIN = 'https://return-path.invalid';

const decodedPath = (pathname: string) => {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return null;
  }
};

export const safeReturnPath = (raw: string | null | undefined): string | null => {
  if (!raw || raw.length > MAX_PATH_LENGTH) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || /[\\\u0000-\u001f]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw, PLACEHOLDER_ORIGIN);
  } catch {
    return null;
  }
  const path = url.pathname + url.search + url.hash;
  if (url.origin !== PLACEHOLDER_ORIGIN || path !== raw) return null;
  const decoded = decodedPath(url.pathname);
  if (decoded === null || decoded.startsWith('//')) return null;
  if (AUTH_PATHS.some((prefix) => url.pathname.startsWith(prefix))) return null;

  return path;
};

export const loginPathFor = (pathname: string, search: string) => {
  const params = new URLSearchParams(search);
  const returnTo = pathname !== '/' ? safeReturnPath(pathname) : null;
  if (returnTo) params.set('returnTo', returnTo);
  const query = params.toString();
  return '/auth/login' + (query ? `?${query}` : '');
};

export const returnPathAfterLogin = (search: string) =>
  safeReturnPath(new URLSearchParams(search).get('returnTo')) ?? '/';

export type ContinueTarget = { sameOrigin: boolean; href: string };

export const continueTargetFor = (
  continueUrl: string | null | undefined,
  currentOrigin: string,
): ContinueTarget | null => {
  if (!continueUrl) return null;

  let url: URL;
  try {
    url = new URL(continueUrl);
  } catch {
    return null;
  }
  const allowed = url.origin === currentOrigin || DASHBOARD_ORIGINS.includes(url.origin);
  const path = url.pathname + url.search + url.hash;
  const loginOrSafePath = path.startsWith('/auth/login') && !path.startsWith('//') ? path : safeReturnPath(path);
  if (!allowed || !loginOrSafePath) return null;

  return url.origin === currentOrigin
    ? { sameOrigin: true, href: loginOrSafePath }
    : { sameOrigin: false, href: url.origin + loginOrSafePath };
};

// Firebase only accepts continue URLs on its authorized domains.
export const actionCodeSettingsFor = (origin: string) =>
  DASHBOARD_ORIGINS.includes(origin) ? { url: `${origin}/auth/login` } : undefined;
